function writeBigUInt64LE(buffer: Uint8Array, value: bigint): void {
  const view = new DataView(buffer.buffer, buffer.byteOffset);
  view.setUint32(0, Number(value & BigInt(0xffffffff)), true);
  view.setUint32(4, Number((value >> BigInt(32)) & BigInt(0xffffffff)), true);
}

export class QuickXorHash {
  data: bigint[];
  shiftSoFar: number;
  lengthSoFar: number;
  widthInBits: number;

  constructor() {
    this.shiftSoFar = 0;
    this.lengthSoFar = 0;
    this.widthInBits = 160;
    this.data = Array.from(
      { length: (this.widthInBits - 1) / 64 + 1 },
      () => 0n,
    );
  }

  update(array: Uint8Array, ibStart: number, cbSize: number): void {
    const shift = 11;
    const bitsInLastCell = 32;

    let vectorOffset = this.shiftSoFar % 64;
    let vectorArrayIndex = Math.floor(this.shiftSoFar / 64);
    const iterations = Math.min(cbSize, this.widthInBits);

    for (let i = 0; i < iterations; i++) {
      const isLastCell = vectorArrayIndex === this.data.length - 1;
      const bitsInVectorCell = isLastCell ? bitsInLastCell : 64;

      if (vectorOffset <= bitsInVectorCell - 8) {
        for (let j = ibStart + i; j < cbSize + ibStart; j += this.widthInBits) {
          this.data[vectorArrayIndex] ^=
            BigInt(array[j]) << BigInt(vectorOffset);
        }
      } else {
        const index1 = vectorArrayIndex;
        const index2 = isLastCell ? 0 : vectorArrayIndex + 1;
        const low = bitsInVectorCell - vectorOffset;

        let xoredByte = 0;
        for (let j = ibStart + i; j < cbSize + ibStart; j += this.widthInBits) {
          xoredByte ^= array[j];
        }
        this.data[index1] ^= BigInt(xoredByte) << BigInt(vectorOffset);
        this.data[index2] ^= BigInt(xoredByte) >> BigInt(low);
      }

      vectorOffset += shift;

      while (vectorOffset >= bitsInVectorCell) {
        vectorArrayIndex = isLastCell ? 0 : vectorArrayIndex + 1;
        vectorOffset -= bitsInVectorCell;
      }
    }

    this.shiftSoFar =
      (this.shiftSoFar + shift * (cbSize % this.widthInBits)) %
      this.widthInBits;
    this.lengthSoFar += cbSize;
  }

  digest(): string {
    const lengthBytes = new Uint8Array(8);
    writeBigUInt64LE(lengthBytes, BigInt(this.lengthSoFar));

    const rgb = new Uint8Array((this.widthInBits - 1) / 8 + 1);

    for (let i = 0; i < this.data.length - 1; i++) {
      const chunk = new Uint8Array(8);
      writeBigUInt64LE(chunk, this.data[i]);
      rgb.set(chunk, i * 8);
    }

    const lastChunk = new Uint8Array(8);
    writeBigUInt64LE(lastChunk, this.data[this.data.length - 1]);
    const lastOffset = (this.data.length - 1) * 8;
    rgb.set(lastChunk.subarray(0, rgb.length - lastOffset), lastOffset);

    for (let i = 0; i < lengthBytes.length; i++) {
      rgb[this.widthInBits / 8 - lengthBytes.length + i] ^= lengthBytes[i];
    }

    return btoa(String.fromCharCode(...rgb));
  }

  static async fromFile(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const hasher = new QuickXorHash();
    hasher.update(bytes, 0, bytes.length);
    return hasher.digest();
  }
}
